import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { Editor } from '@tiptap/react';
import { MermaidDiagram } from './mermaid-diagram';
import { mermaidBlockPositions } from './code-block-actions';
import { isMermaidDiagramShown, subscribeMermaidView } from './mermaid-view-store';

const mermaidPluginKey = new PluginKey('mermaidDiagram');

/**
 * Renders a `mermaid` code block as a diagram WHEN the block's toolbar toggle is
 * ON (see mermaid-view-store). A mermaid block is a normal editable code block
 * by default; toggling on mounts a diagram widget for it (and a CSS rule hides
 * the <code> while that widget is present — see globals.css). Toggling off
 * removes the widget and the code shows again.
 *
 * Why a widget decoration and NOT a React node view (same reasoning as
 * code-block-toolbar-extension.ts): CodeBlockLowlight owns the block's content
 * DOM via decorations; wrapping it in a NodeViewContent React node view makes
 * Tiptap call flushSync mid-render — a reentrancy storm that froze the editor.
 * A widget decoration never touches the content DOM, so the highlighter keeps
 * sole ownership. The diagram React tree is mounted into the widget's own
 * container via createRoot (async/scheduled — no synchronous reentrancy), and
 * MermaidDiagram debounces its own (lazy) render.
 */
export const MermaidExtension = Extension.create({
  name: 'mermaidDiagram',

  addProseMirrorPlugins() {
    const editor = this.editor as Editor;

    // Only blocks toggled ON get a diagram widget; the rest stay editable code.
    const buildDecorations = (doc: PMNode) => {
      const decorations = mermaidBlockPositions(doc)
        .filter(({ pos }) => isMermaidDiagramShown(pos))
        .flatMap(({ pos, code }) => {
          const node = doc.nodeAt(pos);
          const end = node ? pos + node.nodeSize : pos + 1;
          return [
            Decoration.widget(
              // +1: render as a child of the <pre> (position: relative), like the toolbar.
              pos + 1,
              () => {
                const container = document.createElement('div');
                container.className = 'mermaid-diagram-widget';
                container.contentEditable = 'false';
                const root: Root = createRoot(container);
                root.render(createElement(MermaidDiagram, { code }));
                (container as HTMLElement & { __destroy?: () => void }).__destroy = () =>
                  queueMicrotask(() => root.unmount());
                return container;
              },
              {
                // Key includes code length so an edited block remounts with
                // fresh source; position keeps it stable across transactions.
                key: `mermaid-diagram-${pos}-${code.length}`,
                side: 1,
                destroy: (node) =>
                  (node as HTMLElement & { __destroy?: () => void }).__destroy?.(),
              },
            ),
            // While the diagram is shown the source is visually collapsed, so the
            // block must not be typeable — mark its DOM non-editable. Reverts when
            // toggled back to code view (decorations rebuild without this).
            Decoration.node(pos, end, { contenteditable: 'false' }),
          ];
        });
      return DecorationSet.create(doc, decorations);
    };

    // The view store lives outside ProseMirror, so a toggle won't dispatch a
    // transaction on its own. Subscribe and nudge an empty meta transaction to
    // force a decoration rebuild when a block is toggled.
    const unsubscribe = subscribeMermaidView(() => {
      if (editor.isDestroyed) return;
      editor.view.dispatch(editor.state.tr.setMeta(mermaidPluginKey, 'viewToggled'));
    });
    // Clean up the subscription with the editor.
    editor.on('destroy', unsubscribe);

    return [
      new Plugin({
        key: mermaidPluginKey,
        state: {
          init: (_, { doc }) => buildDecorations(doc),
          apply(tr, old) {
            return tr.docChanged || tr.getMeta(mermaidPluginKey)
              ? buildDecorations(tr.doc)
              : old.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});
